import supabase from './supabaseClient';

const USERNAME_COOLDOWN_DAYS = 14;
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 30;
const USERNAME_REGEX = /^[a-zA-Z0-9_.\-]+$/;

export const validateUsername = (username) => {
  if (!username) return 'Username is required';
  const trimmed = username.trim();
  if (trimmed.length < USERNAME_MIN_LENGTH) return `Username must be at least ${USERNAME_MIN_LENGTH} characters`;
  if (trimmed.length > USERNAME_MAX_LENGTH) return `Username must be at most ${USERNAME_MAX_LENGTH} characters`;
  if (!USERNAME_REGEX.test(trimmed)) return 'Username can only contain letters, numbers, dots, hyphens, and underscores';
  return null;
};

export const checkUsernameAvailability = async (username, excludeUserId = null) => {
  if (!username || !username.trim()) return false;

  let query = supabase
    .from('user_profiles')
    .select('id')
    .ilike('username', username.trim());

  if (excludeUserId) {
    query = query.neq('id', excludeUserId);
  }

  const { data, error } = await query;
  if (error) {
    console.warn('Username availability check error:', error);
    return false;
  }
  return !data || data.length === 0;
};

export const canChangeUsername = async (userId) => {
  const { data, error } = await supabase
    .from('username_history')
    .select('changed_at')
    .eq('user_id', userId)
    .order('changed_at', { ascending: false })
    .limit(1);

  if (error) {
    // Table might not exist yet
    console.warn('Username history check error:', error);
    return { canChange: true, nextChangeDate: null };
  }

  if (!data || data.length === 0) {
    return { canChange: true, nextChangeDate: null };
  }

  const lastChange = new Date(data[0].changed_at);
  const nextChange = new Date(lastChange.getTime() + USERNAME_COOLDOWN_DAYS * 24 * 60 * 60 * 1000);
  const now = new Date();

  return {
    canChange: now >= nextChange,
    nextChangeDate: nextChange,
    lastChangeDate: lastChange,
    daysRemaining: Math.max(0, Math.ceil((nextChange - now) / (24 * 60 * 60 * 1000))),
  };
};

export const getUsernameHistory = async (userId) => {
  const { data, error } = await supabase
    .from('username_history')
    .select('*')
    .eq('user_id', userId)
    .order('changed_at', { ascending: false });

  if (error) {
    console.warn('Username history fetch error:', error);
    return [];
  }
  return data || [];
};

export const changeUsername = async (userId, newUsername, changedBy = null) => {
  const trimmed = newUsername.trim();
  const validationError = validateUsername(trimmed);
  if (validationError) throw new Error(validationError);

  const isAvailable = await checkUsernameAvailability(trimmed, userId);
  if (!isAvailable) throw new Error('Username is already taken');

  // Get current username
  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('username')
    .eq('id', userId)
    .single();

  if (profileError) throw profileError;

  const oldUsername = profile?.username || null;

  // Update username
  const { error: updateError } = await supabase
    .from('user_profiles')
    .update({ username: trimmed, updated_at: new Date().toISOString() })
    .eq('id', userId);

  if (updateError) throw updateError;

  // Record in history
  try {
    await supabase
      .from('username_history')
      .insert([{
        user_id: userId,
        old_username: oldUsername,
        new_username: trimmed,
        changed_by: changedBy || userId,
        changed_at: new Date().toISOString(),
      }]);
  } catch (historyErr) {
    console.warn('Failed to record username history:', historyErr);
  }

  return trimmed;
};

export const adminChangeUsername = async (adminId, targetUserId, newUsername) => {
  return changeUsername(targetUserId, newUsername, adminId);
};

export { USERNAME_COOLDOWN_DAYS, USERNAME_MIN_LENGTH, USERNAME_MAX_LENGTH };
